import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './MonthCalendar.css';
import './ClientDashboard.css';
import TrainingPlanTable, { DEFAULT_PLAN_ROWS } from './TrainingPlanTable';

const API_URL = '/api';

const DAYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAYS_FULL_EN = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const MONTHS_SHORT_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDisplayDateEn(dateStr) {
  if (!dateStr) return '';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      return `${MONTHS_EN[month - 1]} ${day}, ${year}`;
    }
  } catch {
    // fallback
  }
  return dateStr;
}

function getStartOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function parseWorkoutData(workout) {
  let rows = [];
  let trainingNumber = 1;
  if (!workout) return { rows: [], trainingNumber: 1 };

  const content = workout.content;
  if (Array.isArray(content)) {
    rows = content;
  } else if (typeof content === 'object' && content !== null) {
    if (content.rows && Array.isArray(content.rows)) rows = content.rows;
    if (content.trainingNumber) trainingNumber = Number(content.trainingNumber);
  } else if (typeof content === 'string' && content) {
    try {
      const parsed = JSON.parse(content);
      if (Array.isArray(parsed)) {
        rows = parsed;
      } else if (typeof parsed === 'object' && parsed !== null) {
        if (parsed.rows && Array.isArray(parsed.rows)) rows = parsed.rows;
        if (parsed.trainingNumber) trainingNumber = Number(parsed.trainingNumber);
      }
    } catch (e) {
      rows = [{ id: '1', set: 'A1', exercise: content, reps: '-', tempo: '-', video: '' }];
    }
  }

  // Fallback to title e.g. "Workout 2"
  if (trainingNumber === 1 && workout.title) {
    const match = workout.title.match(/Workout\s*(\d+)/i);
    if (match) {
      trainingNumber = Number(match[1]);
    }
  }

  return { rows, trainingNumber };
}

function ClientDashboard({ auth }) {
  const { dateStr: paramDateStr } = useParams();
  const navigate = useNavigate();

  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Calendar navigation
  const [currentDate, setCurrentDate] = useState(() => {
    const saved = sessionStorage.getItem('calendar_current_date');
    if (saved) {
      const parsed = new Date(saved);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  });
  const selectedDateKey = paramDateStr || null;

  // Persist view mode in localStorage or auto-detect based on screen size
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('calendar_view_mode');
    if (saved === 'week' || saved === 'month') return saved;
    return typeof window !== 'undefined' && window.innerWidth <= 768 ? 'week' : 'month';
  });

  // If dateStr changes from URL (e.g. notification click), update calendar view
  useEffect(() => {
    if (paramDateStr) {
      const parts = paramDateStr.split('-');
      if (parts.length === 3) {
        const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
        if (!isNaN(d.getTime())) {
          setCurrentDate(d);
        }
      }
    }
  }, [paramDateStr]);

  useEffect(() => {
    sessionStorage.setItem('calendar_current_date', currentDate.toISOString());
  }, [currentDate]);

  const handleViewModeChange = (mode) => {
    localStorage.setItem('calendar_view_mode', mode);
    setViewMode(mode);
  };

  const fetchWorkouts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API_URL}/client/workouts`, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      setWorkouts(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error loading workouts');
    } finally {
      setLoading(false);
    }
  }, [auth?.token]);

  useEffect(() => {
    fetchWorkouts();
  }, [fetchWorkouts]);

  // Real-time SSE comment updates for client workouts
  useEffect(() => {
    const handleCommentAdded = (e) => {
      const data = e.detail;
      if (!data?.comment || !data?.workout_id) return;
      if (data.workout_user_id && auth?.user?.id && Number(data.workout_user_id) !== Number(auth.user.id)) {
        return;
      }
      setWorkouts((prev) => {
        let found = false;
        const updated = prev.map((w) => {
          if (w.id === data.workout_id || (data.workout_date && w.dateKey === data.workout_date)) {
            found = true;
            const currentComments = w.comments || [];
            if (currentComments.some((c) => c.id === data.comment.id)) return w;
            return {
              ...w,
              comments: [...currentComments, data.comment],
              userComment: data.comment.authorRole === 'CLIENT' ? data.comment.text : w.userComment
            };
          }
          return w;
        });

        // If workout wasn't in state yet, trigger background fetch to get full workout
        if (!found) {
          fetchWorkouts();
        }
        return updated;
      });
    };

    const handleWorkoutUpdated = (e) => {
      const data = e.detail;
      if (!data?.workout) return;
      if (data.client_id && auth?.user?.id && Number(data.client_id) !== Number(auth.user.id)) {
        return;
      }

      setWorkouts((prev) => {
        const existingIndex = prev.findIndex(
          (w) => (data.workout_id && w.id === data.workout_id) || (data.workout_date && w.dateKey === data.workout_date)
        );
        if (existingIndex >= 0) {
          const copy = [...prev];
          copy[existingIndex] = {
            ...copy[existingIndex],
            ...data.workout
          };
          return copy;
        } else {
          return [...prev, data.workout].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
        }
      });
    };

    const handleWorkoutDeleted = (e) => {
      const data = e.detail;
      if (data?.client_id && auth?.user?.id && Number(data.client_id) !== Number(auth.user.id)) {
        return;
      }
      setWorkouts((prev) =>
        prev.filter((w) => {
          if (data?.workout_id && w.id === data.workout_id) return false;
          if (data?.workout_date && w.dateKey === data.workout_date) return false;
          return true;
        })
      );
    };

    window.addEventListener('tp:comment_added', handleCommentAdded);
    window.addEventListener('tp:workout_updated', handleWorkoutUpdated);
    window.addEventListener('tp:workout_deleted', handleWorkoutDeleted);
    return () => {
      window.removeEventListener('tp:comment_added', handleCommentAdded);
      window.removeEventListener('tp:workout_updated', handleWorkoutUpdated);
      window.removeEventListener('tp:workout_deleted', handleWorkoutDeleted);
    };
  }, [auth?.user?.id, fetchWorkouts]);

  // Map workouts by date
  const workoutsMap = useMemo(() => {
    const map = {};
    workouts.forEach((w) => {
      if (w.dateKey) {
        map[w.dateKey] = w;
      }
    });
    return map;
  }, [workouts]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const handlePrev = () => {
    if (viewMode === 'week') {
      const prevWeek = new Date(currentDate);
      prevWeek.setDate(prevWeek.getDate() - 7);
      setCurrentDate(prevWeek);
    } else {
      setCurrentDate(new Date(year, month - 1, 1));
    }
  };

  const handleNext = () => {
    if (viewMode === 'week') {
      const nextWeek = new Date(currentDate);
      nextWeek.setDate(nextWeek.getDate() + 7);
      setCurrentDate(nextWeek);
    } else {
      setCurrentDate(new Date(year, month + 1, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const today = new Date();

  // ====== WEEK VIEW DAYS ======
  const weekStart = getStartOfWeek(currentDate);
  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dayPadded = String(d.getDate()).padStart(2, '0');
    const dateKey = `${y}-${m}-${dayPadded}`;
    const isToday =
      today.getDate() === d.getDate() &&
      today.getMonth() === d.getMonth() &&
      today.getFullYear() === d.getFullYear();

    weekDays.push({
      date: d,
      day: d.getDate(),
      dayName: DAYS_EN[i],
      dayFullName: DAYS_FULL_EN[i],
      monthName: MONTHS_SHORT_EN[d.getMonth()],
      monthLongName: MONTHS_EN[d.getMonth()],
      year: y,
      dateKey,
      isToday,
      isCurrentMonth: true
    });
  }

  const startDay = weekDays[0];
  const endDay = weekDays[6];
  let weekRangeTitle = '';
  if (startDay.year === endDay.year) {
    if (startDay.date.getMonth() === endDay.date.getMonth()) {
      weekRangeTitle = `${startDay.monthName} ${startDay.day} – ${endDay.day}, ${startDay.year}`;
    } else {
      weekRangeTitle = `${startDay.monthName} ${startDay.day} – ${endDay.monthName} ${endDay.day}, ${startDay.year}`;
    }
  } else {
    weekRangeTitle = `${startDay.monthName} ${startDay.day}, ${startDay.year} – ${endDay.monthName} ${endDay.day}, ${endDay.year}`;
  }

  // Calendar days
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();

  let startDayOfWeek = firstDayOfMonth.getDay() - 1;
  if (startDayOfWeek === -1) startDayOfWeek = 6;

  const prevMonthLastDay = new Date(year, month, 0).getDate();
  const prevMonthDays = [];
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    prevMonthDays.push({
      day: prevMonthLastDay - i,
      dateKey: null,
      isCurrentMonth: false
    });
  }

  const currentMonthDays = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const isToday =
      today.getDate() === d &&
      today.getMonth() === month &&
      today.getFullYear() === year;

    const monthPadded = String(month + 1).padStart(2, '0');
    const dayPadded = String(d).padStart(2, '0');
    const dateKey = `${year}-${monthPadded}-${dayPadded}`;

    currentMonthDays.push({
      day: d,
      dateKey,
      isCurrentMonth: true,
      isToday
    });
  }

  const totalCells = prevMonthDays.length + currentMonthDays.length;
  const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
  const nextMonthDays = [];
  for (let d = 1; d <= remainingCells; d++) {
    nextMonthDays.push({
      day: d,
      dateKey: null,
      isCurrentMonth: false
    });
  }

  const allCalendarDays = [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];

  const handleOpenDay = (dateKey) => {
    const workout = workoutsMap[dateKey];
    if (!workout) return;
    navigate(`/client/workout/${dateKey}`);
    setSuccessMsg('');
    setError(null);
  };

  const handleSaveWorkoutStatus = useCallback(async (dateKey, planData) => {
    const workout = workoutsMap[dateKey];
    if (!workout) return;

    try {
      let statusEnum = 'SCHEDULED';
      if (planData.status === 'completed' || planData.status === 'DONE') statusEnum = 'DONE';
      else if (planData.status === 'skipped' || planData.status === 'SKIPPED') statusEnum = 'SKIPPED';

      const res = await axios.patch(
        `${API_URL}/client/workouts/${workout.id}/status`,
        {
          status: statusEnum
        },
        {
          headers: { Authorization: `Bearer ${auth?.token}` }
        }
      );

      setWorkouts(prev => prev.map(w => w.id === res.data.id ? res.data : w));
    } catch (err) {
      setError(err.response?.data?.error || 'Error updating workout status');
    }
  }, [auth?.token, workoutsMap]);

  const handleAddWorkoutComment = useCallback(async (commentText) => {
    const workout = workoutsMap[selectedDateKey];
    if (!workout) {
      throw new Error('Workout not found.');
    }

    const res = await axios.post(
      `${API_URL}/workouts/${workout.id}/comments`,
      { text: commentText },
      { headers: { Authorization: `Bearer ${auth?.token}` } }
    );

    const currentComments = workout.comments || [];
    const updatedWorkout = {
      ...workout,
      userComment: commentText,
      user_comment: commentText,
      comments: currentComments.some(c => c.id === res.data.id)
        ? currentComments
        : [...currentComments, res.data]
    };

    setWorkouts(prev => prev.map(w => w.id === workout.id ? updatedWorkout : w));
    return res.data;
  }, [auth?.token, selectedDateKey, workoutsMap]);

  if (selectedDateKey) {
    if (loading) {
      return (
        <div className="client-workout-detail-container">
          <div className="loading" style={{ padding: '40px', textAlign: 'center' }}>Loading workout...</div>
        </div>
      );
    }

    const activeWorkout = workoutsMap[selectedDateKey];
    if (!activeWorkout) {
      return (
        <div className="client-workout-detail-container">
          <div className="training-table-top-bar" style={{ marginBottom: '20px' }}>
            <div className="training-table-top-left">
              <button
                type="button"
                className="btn-cal-arrow"
                onClick={() => navigate('/client')}
                title="Back to Calendar"
                aria-label="Back to Calendar"
              >
                &lt;
              </button>
              <span className="training-date-label">
                {formatDisplayDateEn(selectedDateKey)}
              </span>
            </div>
          </div>
          <div className="error">
            Workout not found or has been deleted.
          </div>
        </div>
      );
    }

    const parsedData = parseWorkoutData(activeWorkout);
    const rows = parsedData.rows.length > 0 ? parsedData.rows : DEFAULT_PLAN_ROWS;
    const rawSt = activeWorkout?.status || 'SCHEDULED';
    const isCompletedVal = rawSt === 'DONE' || rawSt === 'completed';
    const isSkippedVal = rawSt === 'SKIPPED' || rawSt === 'skipped';

    return (
      <div className="client-workout-detail-container">
        {error && <div className="error">{error}</div>}
        {successMsg && <div className="success-banner">{successMsg}</div>}

        <TrainingPlanTable
          key={selectedDateKey}
          clientName={auth.user?.name || auth.user?.email}
          dateStr={selectedDateKey}
          initialData={{
            id: activeWorkout?.id,
            trainingNumber: parsedData.trainingNumber,
            status: isCompletedVal ? 'completed' : isSkippedVal ? 'skipped' : 'pending',
            rows,
            userComment: activeWorkout?.userComment || activeWorkout?.user_comment || '',
            comments: activeWorkout?.comments || []
          }}
          onSave={handleSaveWorkoutStatus}
          onAddComment={handleAddWorkoutComment}
          onBack={() => navigate('/client')}
          auth={auth}
          canEditPlan={false}
        />
      </div>
    );
  }

  return (
    <div className="client-dashboard-container">
      {error && <div className="error">{error}</div>}

      <div className="calendar-container">
        <div className="calendar-header">
          <div className="calendar-nav-controls">
            <button
              type="button"
              onClick={handlePrev}
              className="btn-cal-arrow"
              title={viewMode === 'week' ? 'Previous week' : 'Previous month'}
            >
              &lt;
            </button>
            <span className="calendar-current-month-text">
              {viewMode === 'week' ? weekRangeTitle : `${MONTHS_EN[month]} ${year}`}
            </span>
            <button
              type="button"
              onClick={handleNext}
              className="btn-cal-arrow"
              title={viewMode === 'week' ? 'Next week' : 'Next month'}
            >
              &gt;
            </button>
            <button
              type="button"
              onClick={handleToday}
              className="btn-cal-today"
              title="Jump to today"
            >
              Today
            </button>
          </div>

          <div className="calendar-view-toggle">
            <button
              type="button"
              className={`btn-view-toggle ${viewMode === 'week' ? 'active' : ''}`}
              onClick={() => handleViewModeChange('week')}
            >
              Week
            </button>
            <button
              type="button"
              className={`btn-view-toggle ${viewMode === 'month' ? 'active' : ''}`}
              onClick={() => handleViewModeChange('month')}
            >
              Month
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading" style={{ padding: '40px' }}>Loading calendar...</div>
        ) : viewMode === 'week' ? (
          /* ====== WEEK VIEW (VARIANT A: VERTICAL LIST / CARDS) ====== */
          <div className="calendar-week-list">
            {weekDays.map((item) => {
              const workout = item.dateKey ? workoutsMap[item.dateKey] : null;
              const hasWorkout = Boolean(workout);
              const status = workout?.status || 'SCHEDULED';
              const isCompleted = status === 'DONE' || status === 'completed';

              const todayDateKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
              const isPast = Boolean(item.dateKey && item.dateKey < todayDateKey);

              let cellStatus = 'pending';
              if (isCompleted) {
                cellStatus = 'completed';
              } else if (isPast) {
                cellStatus = 'missed';
              } else {
                cellStatus = 'pending';
              }

              const statusClass = hasWorkout ? `status-${cellStatus}` : '';
              const parsedW = parseWorkoutData(workout);
              const trainingNum = parsedW.trainingNumber || 1;

              return (
                <div
                  key={item.dateKey}
                  className={`calendar-week-card ${item.isToday ? 'is-today' : ''} ${hasWorkout ? 'has-plan ' + statusClass : 'no-plan'}`}
                  onClick={() => hasWorkout && handleOpenDay(item.dateKey)}
                  style={{ cursor: hasWorkout ? 'pointer' : 'default' }}
                >
                  <span className="week-card-date">{item.monthName} {item.day}</span>
                  <div className="week-card-center-header">
                    <div className="week-card-day-info">
                      <span className="week-card-day-name">
                        {item.dayFullName}
                      </span>
                    </div>
                  </div>

                  <div className="week-card-content-area">
                    {hasWorkout ? (
                      <div className="week-card-plan-center">
                        <div className={`training-number-circle ${statusClass}`}>
                          {trainingNum}
                        </div>
                      </div>
                    ) : (
                      <div className="week-card-empty-state">
                        <span className="week-card-rest-text">Rest</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ====== MONTH VIEW (STANDARD GRID) ====== */
          <div className="calendar-grid">
            {DAYS_EN.map((dayName) => (
              <div key={dayName} className="calendar-day-name">
                {dayName}
              </div>
            ))}

            {allCalendarDays.map((item, idx) => {
              const workout = item.dateKey ? workoutsMap[item.dateKey] : null;
              const hasWorkout = Boolean(workout);
              const status = workout?.status || 'SCHEDULED';
              const isCompleted = status === 'DONE' || status === 'completed';

              const todayDateKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
              const isPast = Boolean(item.dateKey && item.dateKey < todayDateKey);

              let cellStatus = 'pending';
              if (isCompleted) {
                cellStatus = 'completed';
              } else if (isPast) {
                cellStatus = 'missed';
              } else {
                cellStatus = 'pending';
              }

              const statusClass = hasWorkout ? `status-${cellStatus}` : '';
              const parsedW = parseWorkoutData(workout);
              const trainingNum = parsedW.trainingNumber || 1;

              return (
                <div
                  key={idx}
                  className={`calendar-day-cell ${!item.isCurrentMonth ? 'other-month' : ''} ${item.isToday ? 'is-today' : ''} ${hasWorkout ? 'has-plan ' + statusClass : ''}`}
                  onClick={() => item.isCurrentMonth && hasWorkout && handleOpenDay(item.dateKey)}
                  style={{
                    cursor: item.isCurrentMonth && hasWorkout ? 'pointer' : 'default',
                    opacity: item.isCurrentMonth && !hasWorkout ? 0.85 : 1
                  }}
                  title={item.isCurrentMonth && !hasWorkout ? 'Rest' : `Workout - Click to view`}
                >
                  <div className="calendar-day-header">
                    <span className="calendar-day-number">
                      {item.day}
                    </span>
                  </div>

                  <div className="calendar-day-content">
                    {item.isCurrentMonth && (
                      hasWorkout ? (
                        <div className={`client-training-badge ${statusClass}`}>
                          <div className={`training-number-circle ${statusClass}`}>
                            {trainingNum}
                          </div>
                        </div>
                      ) : (
                        <span className="no-plan-hint">Rest</span>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientDashboard;
